import React, { useState, useEffect } from 'react';
import { PageTitle } from '@cordata/buffet';
import {
  Button,
  ButtonGroup,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useSlice from '~/utils/slice/hook';
import SlicePicker from '~/components/Slice/Picker';
import {
  referenceAdminInstance as refAdmin,
  useQueryParam,
  toastError,
  toastSuccess,
} from '~/utils';

import Form from '~/components/SideNavConfiguration/Form';
import Preview from '~/components/SideNavConfiguration/Preview';

const SideNavConfiguration = () => {
  const params = useParams();
  const location = useLocation();
  const {
    fetch, fetchAll, currentSlice, setCurrentSlice, attachToItem,
  } = useSlice();
  const [createSideNavItem] = useState(() => location.state && location.state.createSideNavItem);

  const [currentModule, setCurrentModule] = useQueryParam('module', (createSideNavItem && createSideNavItem.appModule) || 'patient');
  const [sliceType, setSliceType] = useQueryParam('sliceType', 'system');
  const [sliceId, setSliceId] = useQueryParam('sliceId');

  const [itemsStatus, setItemsStatus] = useState('loading');
  const [items, setItems] = useState([]);
  const [currentItemId, setCurrentItemId] = useQueryParam('item');
  const [currentItem, setCurrentItem] = useState();

  const [isEditingList, setIsEditingList] = useState(true);

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = useState();
  const [itemToDelete, setItemToDelete] = useState();

  const [licenseTierLabels, setLicenseTierLabels] = useState([]);
  const [licenseTierOptions, setLicenseTierOptions] = useState([]);

  const reloadItems = () => {
    setItemsStatus('loading');

    return refAdmin
      .get(`/reference-nav-item/preview?ref-data-type=${currentModule}&$slice_type=${currentSlice.type}&$slice_id=${currentSlice.id}&page=1&page-size=1000&sort=default-sort-order`)
      .then(r => r.data.data)
      .then((itemsData) => {
        setItems(itemsData);
        setItemsStatus('success');
      })
      .catch(() => {
        setItemsStatus('success');
        toastError('Error loading nav items');
      });
  };

  const editItem = (item) => {
    attachToItem(item)
      .then(setCurrentItem)
      .catch(() => toastError('Error loading item details'));
  };

  useEffect(() => {
    if (itemsStatus === 'success' && currentItemId && (!currentItem || currentItem.id !== currentItemId)) {
      const found = items.find(i => i.id === currentItemId);
      if (found) {
        editItem(found);
      }
    }
  }, [itemsStatus, currentItemId, items]);

  useEffect(() => {
    if (itemsStatus === 'success') {
      if (currentItem) {
        setCurrentItemId(currentItem.id);
      } else {
        setCurrentItemId(undefined);
      }
    }
  }, [itemsStatus, currentItem]);

  const confirmDelete = (item) => {
    setItemToDelete(item);
    const message = (
      <>
        <p>
          The item <strong>{item.attributes.navItemLabel}</strong> will
          be <span className="text-danger font-weight-bold">permanently deleted</span>.
        </p>
        <p className="text-muted small mb-0">
          If this item has children, they may become orphans.
        </p>
      </>
    );
    setConfirmDeleteMessage(message);
    setIsConfirmDeleteModalOpen(true);
  };

  const deleteItem = () => {
    if (itemToDelete && itemToDelete.id) {
      refAdmin
        .delete(`/reference-nav-item/${itemToDelete.id}`)
        .then(() => {
          reloadItems();
          setCurrentItem(undefined);
        })
        .catch((err) => {
          const serverMessage = err && err.response
            && err.response.data && err.response.data.message;
          toastError(serverMessage || 'Error deleting item');
        });
    } else {
      reloadItems();
      setCurrentItem(undefined);
    }

    setIsConfirmDeleteModalOpen(false);
  };

  const cancelEdit = (e) => {
    if (e) e.preventDefault();

    if (!currentItem.id) {
      reloadItems();
    }

    setCurrentItem(undefined);
  };

  useEffect(() => {
    fetchAll();

    if (sliceId && sliceType && sliceType !== 'system' && sliceType !== 'all') {
      fetch(sliceType, { id: sliceId })
        .then(({ data: { data: entity } }) => {
          setCurrentSlice({
            type: sliceType,
            id: sliceId,
            lineage: {
              market: entity.attributes.marketUid,
              specialty: entity.attributes.specialtyUid,
              subSpecialty: entity.attributes.subSpecialtyUid,
              organization: entity.attributes.orgUid,
              division: entity.attributes.divisionUid,
            },
          });
        })
        .catch(() => {
          setCurrentSlice({ type: sliceType, id: sliceId });
        });
    } else {
      setCurrentSlice(sliceType || 'system');
    }

    refAdmin
      .get('/reference-license-tier?page=1&page-size=1000&sort=license-tier-level')
      .then(r => r.data.data)
      .then((r) => {
        setLicenseTierLabels(r.map(rr => rr.attributes.licenseTierLabel));
        setLicenseTierOptions(r.map(rr => ({
          label: rr.attributes.licenseTierLabel,
          value: rr.attributes.licenseTierLevel,
        })));
      })
      .catch(() => toastError('Error loading license tiers'));
  }, []);

  useEffect(() => {
    setCurrentItem(undefined);

    if (currentSlice.type === 'all' ||
        currentSlice.type === 'system' ||
        currentSlice.id) {
      reloadItems();
    }
  }, [currentSlice, currentModule]);

  useEffect(() => {
    setSliceType(currentSlice.type || 'system');
    setSliceId(currentSlice.id || undefined);
  }, [currentSlice]);

  return (
    <>
      <PageTitle
        action={
          <Button
            color="info"
            target="_blank"
            href="https://docs.cordata.io/books/features/page/configuration-67c"
          >
            <FontAwesomeIcon icon="external-link-alt" />

            <span className="d-none d-md-inline ml-2">See Documentation</span>
          </Button>
        }
      >
        Configure Side Nav Items
      </PageTitle>

      <SlicePicker slices={['system', 'market', 'specialty', 'sub-specialty', 'organization', 'division']} />

      <ButtonGroup className="w-100 mt-3 mb-4">
        <Button
          color="primary"
          onClick={() => setCurrentModule('patient')}
          active={currentModule === 'patient'}
        >
          Patient
        </Button>

        <Button
          color="primary"
          onClick={() => setCurrentModule('reports')}
          active={currentModule === 'reports'}
        >
          Reports
        </Button>
      </ButtonGroup>

      <Container fluid>
        <Row>
          <Col>
            <Preview
              items={items}
              editItem={editItem}
              reloadItems={reloadItems}
              isEditing={isEditingList}
              setIsEditing={setIsEditingList}
              licenseTierLabels={licenseTierLabels}
              createSideNavItem={createSideNavItem}
            />
          </Col>

          <Col>
            {currentItem && (
              <Form
                key={currentItem.id || 'new'}
                data={currentItem}
                items={items}
                reloadItems={reloadItems}
                confirmDelete={confirmDelete}
                cancelEdit={cancelEdit}
                currentModule={currentModule}
                licenseTierOptions={licenseTierOptions}
                pageSlice={currentSlice}
                onCreateOverride={(itemsToCreate) => {
                  if (itemsToCreate.length === 0) return;

                  const posts = itemsToCreate.map(item => refAdmin.post('/reference-nav-item', {
                    ...item.attributes,
                    appModule: currentModule,
                    $slice_type: currentSlice.type,
                    $slice_id: currentSlice.id,
                  }));

                  Promise.all(posts)
                    .then(() => toastSuccess(`Created ${itemsToCreate.length} override${itemsToCreate.length > 1 ? 's' : ''}`))
                    .then(reloadItems)
                    .then(() => cancelEdit())
                    .catch(() => toastError('Error creating override'));
                }}
                {...params}
              />
            )}
          </Col>
        </Row>
      </Container>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ModalHeader>Are you sure?</ModalHeader>

        <ModalBody style={{ maxHeight: '50vh', overflowY: 'scroll' }}>
          {confirmDeleteMessage}
        </ModalBody>

        <ModalFooter>
          <Button outline color="tertiary" type="cancel" onClick={() => setIsConfirmDeleteModalOpen(false)}>
            Cancel
          </Button>

          <Button color="danger" onClick={deleteItem}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SideNavConfiguration;
